/* PUTAOYAO depth v11 — progressive async scan, cached metrics, visible-only RAF. */
(() => {
  const existing = window.pyScrollDepth;
  if (existing && existing.version === 11) {
    existing.scan();
    return;
  }
  if (existing && typeof existing.destroy === 'function') existing.destroy();

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const heroAnchors = '.py-homev2__closing-media,.py-homev2__hero-media,.py-craftv2__hero-media,.py-subhero,.py-mainhero__media,.py-brandhero__media,.py-collectionhero__media,.py-realcraft__hero,.py-tv__hero-media';
  const largeAnchors = '.py-homev2__craft-media,.py-homev2__story-image,.py-person__visual,.py-base__visual,.py-ipdp__media--main,.py-pdp__media--main,.py-home .py-media,.py-vtl__media,.py-vtl__image,.py-craftv2__detail-media';
  const excluded = '.py-homev2__hero,.py-homev2__product,.py-editorial-card,.py-ipdp__gallery,.py-pdp__gallery,dialog,[role="dialog"],[data-no-depth],.py-craft__stage,.py-craft-film,.py-craft__film,.py-honors__grid,.py-patents__grid';

  const items = new Map();
  const anchorItems = new Map();
  const dirty = new Set();
  const aborter = new AbortController();
  const signal = aborter.signal;
  let raf = 0;
  let scanRaf = 0;
  let domObserver = null;
  const pendingScanRoots = new Set();
  let mobile = innerWidth < 750;

  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

  function request() {
    if (!raf && !document.hidden) raf = requestAnimationFrame(render);
  }

  function setVisible(item, visible) {
    item.visible = visible;
    item.el.classList.toggle('is-depth-visible', visible && !reduced.matches);
  }

  function resetItem(item) {
    item.el.style.setProperty('--py-depth-y', '0px');
    item.el.style.setProperty('--py-depth-scale', '1');
    item.lastY = 0;
    item.lastScale = 1;
  }

  function markDirty(item) {
    if (!item || !item.el.isConnected) return;
    dirty.add(item);
    request();
  }

  function readMetrics(item) {
    const el = item.el;
    const rect = item.anchor.getBoundingClientRect();
    const h = Math.max(rect.height || el.clientHeight || item.anchor.clientHeight || 1, 1);
    const w = Math.max(rect.width || el.clientWidth || item.anchor.clientWidth || 1, 1);
    const style = getComputedStyle(el);
    const contain = style.objectFit === 'contain';
    const fraction = item.hero ? (mobile ? .05 : .14) : (mobile ? .04 : .11);
    const limit = item.hero ? (mobile ? 36 : 130) : (mobile ? 26 : 90);
    let distance = Math.min(limit, h * fraction);

    if (contain) {
      const nw = el.naturalWidth || el.videoWidth || 0;
      const nh = el.naturalHeight || el.videoHeight || 0;
      const drawn = (nw && nh) ? Math.min(w / nw, h / nh) * nh : h;
      distance = Math.max(0, Math.min(mobile ? 20 : 32, (h - drawn) * .35));
    }

    item.height = h;
    item.distance = distance * (contain ? 1.3 : 1.625);
    item.scale = (reduced.matches || contain) ? 1 : 1 + (distance * 2 + 4) / h;
    item.contain = contain;
  }

  function render() {
    raf = 0;
    if (document.hidden) return;

    const vh = Math.max(innerHeight, 1);
    const writes = [];

    if (dirty.size) {
      dirty.forEach(item => {
        if (items.has(item.el) && item.el.isConnected) readMetrics(item);
      });
      dirty.clear();
    }

    if (reduced.matches) {
      items.forEach(item => {
        if (item.el.isConnected && (item.lastY !== 0 || item.lastScale !== 1)) writes.push({ item, y: 0, scale: 1 });
      });
    } else {
      items.forEach(item => {
        if (!item.el.isConnected) {
          remove(item.el);
          return;
        }
        if (!item.visible) return;

        const r = item.anchor.getBoundingClientRect();
        const progress = clamp((vh * .52 - (r.top + r.height * .5)) / (vh * .58), -1, 1);
        // Keep the previous overscan scale. Extra travel is allowed where the
        // corresponding image edge is already outside the viewport.
        const overscan = (item.scale - 1) * item.height / 2;
        const desiredY = progress * item.distance;
        const y = item.contain ? desiredY : clamp(desiredY,
          -overscan - Math.max(0, r.bottom - vh),
          overscan + Math.max(0, -r.top));
        writes.push({ item, y, scale: item.scale });
      });
    }

    writes.forEach(({ item, y, scale }) => {
      if (Math.abs((item.lastY ?? 9999) - y) > .12) {
        item.el.style.setProperty('--py-depth-y', `${y.toFixed(2)}px`);
        item.lastY = y;
      }
      if (Math.abs((item.lastScale ?? 9999) - scale) > .0005) {
        item.el.style.setProperty('--py-depth-scale', scale.toFixed(4));
        item.lastScale = scale;
      }
    });
  }

  const intersectionObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const set = anchorItems.get(entry.target);
      if (!set) return;
      set.forEach(item => setVisible(item, entry.isIntersecting));
    });
    request();
  }, { rootMargin: '180px 0px', threshold: 0 });

  const resizeObserver = new ResizeObserver(entries => {
    entries.forEach(entry => {
      const item = items.get(entry.target);
      if (item) markDirty(item);
    });
  });

  function remove(el) {
    const item = items.get(el);
    if (!item) return;
    resizeObserver.unobserve(item.el);
    const set = anchorItems.get(item.anchor);
    if (set) {
      set.delete(item);
      if (!set.size) {
        intersectionObserver.unobserve(item.anchor);
        anchorItems.delete(item.anchor);
      }
    }
    items.delete(el);
    dirty.delete(item);
  }

  function add(el, anchor, hero) {
    if (!el || items.has(el) || el.closest(excluded) || (el.tagName === 'VIDEO' && el.controls)) return;
    if (!anchor || anchor === el || anchor.closest('header.header,footer')) return;

    const item = {
      el,
      anchor,
      hero,
      visible: true,
      distance: 0,
      scale: 1,
      lastY: null,
      lastScale: null
    };

    items.set(el, item);
    let set = anchorItems.get(anchor);
    if (!set) {
      set = new Set();
      anchorItems.set(anchor, set);
      intersectionObserver.observe(anchor);
    }
    set.add(item);

    anchor.classList.add('py-depth-anchor');
    el.classList.add('py-depth-media', 'is-depth-visible');
    resizeObserver.observe(el);
    dirty.add(item);

    const refresh = () => markDirty(item);
    el.addEventListener('load', refresh, { once: true, signal });
    el.addEventListener('loadedmetadata', refresh, { once: true, signal });
  }

  function queueScan(root = document) {
    if (root?.nodeType === 1 || root === document) pendingScanRoots.add(root);
    if (scanRaf) return;
    scanRaf = requestAnimationFrame(() => {
      scanRaf = 0;
      const roots = [...pendingScanRoots];
      pendingScanRoots.clear();
      roots.forEach(node => scan(node));
    });
  }

  function scan(root = document) {
    const queryAll = selector => {
      const nodes = [];
      if (root.nodeType === 1 && root.matches?.(selector)) nodes.push(root);
      root.querySelectorAll?.(selector).forEach(node => nodes.push(node));
      return nodes;
    };

    queryAll(heroAnchors).forEach(anchor => {
      anchor.querySelectorAll('img,video').forEach(el => add(el, anchor, true));
    });

    queryAll(largeAnchors).forEach(anchor => {
      anchor.querySelectorAll('img,video').forEach(el => add(el, anchor, false));
    });

    queryAll('#MainContent img,#MainContent video:not([controls])').forEach(el => {
      if (items.has(el) || el.closest(excluded)) return;
      const parent = el.parentElement;
      if (!parent) return;
      const classes = String(parent.className || '');
      const hero = /(?:__hero|__hero-media|__closing|__cover)(?:\s|$)/.test(classes);
      const photo = /(?:__visual|__image|__photo|__media)(?:\s|$)/.test(classes);
      if (!hero && !photo) return;

      const mediaOnly = [...parent.children].every(child => ['IMG', 'VIDEO', 'PICTURE', 'SOURCE', 'I'].includes(child.tagName));
      if (!mediaOnly && !hero) return;

      const r = parent.getBoundingClientRect();
      if (hero && r.width > innerWidth * .65 && r.height > 220) add(el, parent, true);
      else if (photo && mediaOnly && r.width >= Math.min(380, innerWidth * .82) && r.height >= 220) add(el, parent, false);
    });

    request();
  }

  function onResize() {
    mobile = innerWidth < 750;
    items.forEach(item => dirty.add(item));
    request();
  }

  function onReducedChange() {
    items.forEach(item => {
      item.el.classList.toggle('is-depth-visible', item.visible && !reduced.matches);
      dirty.add(item);
      if (reduced.matches) resetItem(item);
    });
    request();
  }

  function cleanupSection(event) {
    [...items.keys()].forEach(el => {
      if (event.target.contains(el)) remove(el);
    });
  }

  function destroy() {
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    aborter.abort();
    intersectionObserver.disconnect();
    resizeObserver.disconnect();
    reduced.removeEventListener('change', onReducedChange);
    items.forEach(item => resetItem(item));
    items.clear();
    anchorItems.clear();
    dirty.clear();
    if (window.pyScrollDepth?.version === 11) delete window.pyScrollDepth;
  }

  addEventListener('scroll', request, { passive: true, signal });
  addEventListener('resize', onResize, { passive: true, signal });
  addEventListener('load', () => queueScan(document), { once: true, signal });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) request(); }, { signal });
  document.addEventListener('shopify:section:load', event => queueScan(event.target), { signal });
  document.addEventListener('shopify:section:unload', cleanupSection, { signal });
  reduced.addEventListener('change', onReducedChange);

  if ('MutationObserver' in window && document.documentElement) {
    domObserver = new MutationObserver(entries => {
      entries.forEach(entry => entry.addedNodes.forEach(node => {
        if (node.nodeType === 1) queueScan(node);
      }));
    });
    domObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  window.pyScrollDepth = { version: 11, scan, destroy };
  scan(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => queueScan(document), { once: true, signal });
  }
})();
